import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEventStore, useGuestStore } from "store";
import * as SpacesService from "libs/digitalocean/SpacesService";
import { TGuest } from "services/types";
import { useNavigate } from "react-router-dom";
import CdtAppPublicApi from "cdt-app-public-api/dist";
import { FormattedMessage, useIntl } from "react-intl";
import { Close } from "assets/svg";
import ImageCropper from "../components/ImageCropper";

const IMAGE_SIZE_LIMIT = 10 * 1024 * 1024;

export default function GuestForm() {
  const { event, theme } = useEventStore();
  const { guest, setGuest } = useGuestStore();
  const navigate = useNavigate();
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [guestName, setGuestName] = useState("");
  const [photoUploadStatus, setPhotoUploadStatus] = useState<
    "idle" | "uploading" | "success"
  >("idle");
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasExistingPhoto, setHasExistingPhoto] = useState(false);

  const [error, setError] = useState("");

  const generateGuestId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const [guestId] = useState(() => {
    return guest?.id || generateGuestId();
  });

  console.log("Guest ID:", guestId);

  const photoStoragePath = useMemo(() => {
    return `guests/${event?.id}/${guestId}.jpg`;
  }, [event?.id, guestId]);

  useEffect(() => {
    if (!guest) return;
    setIsEditMode(true);
    setGuestName(guest.name || "");

    if (!guest.photoStoragePath) return;
    setHasExistingPhoto(true);
    setPhotoUploadStatus("success");

    const loadExistingPhoto = async () => {
      try {
        const photoUrl = await SpacesService.getFileUrl(
          guest.photoStoragePath as string
        );
        setPhotoPreview(photoUrl);
      } catch (err) {
        console.error("Failed to load existing photo preview:", err);
      }
    };

    loadExistingPhoto();
  }, [guest]);

  const createGuest = useCallback(async (guestData: any) => {
    const cdtAppClient = new CdtAppPublicApi();
    return await cdtAppClient.createGuest(guestData);
  }, []);

  const updateGuest = useCallback(async (id: string, guestData: any) => {
    const cdtAppClient = new CdtAppPublicApi();
    return await cdtAppClient.updateGuest(id, guestData);
  }, []);

  const handlePhotoButton = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      fileInputRef.current?.click();
      e?.preventDefault();
    },
    []
  );

  const handlePhotoSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        setError(
          intl.formatMessage({
            id: "error.guestForm.invalidFileType",
            defaultMessage: "Por favor, selecione um arquivo de imagem válido",
          })
        );
        return;
      }
      if (file.size > IMAGE_SIZE_LIMIT) {
        setError(
          intl.formatMessage({
            id: "error.guestForm.fileTooLarge",
            defaultMessage: "O arquivo deve ter no máximo 10MB",
          })
        );
        return;
      }
      setSelectedPhoto(file);
      setError("");
      setShowCropper(true);
    },
    [intl]
  );

  const handleCropFinish = useCallback(
    async (croppedFile: File) => {
      setShowCropper(false);

      const previewUrl = URL.createObjectURL(croppedFile);
      setPhotoPreview(previewUrl);
      setHasExistingPhoto(false);

      setPhotoUploadStatus("uploading");
      try {
        await SpacesService.uploadFile(croppedFile, photoStoragePath);
        setPhotoUploadStatus("success");
      } catch (error) {
        console.error("Upload failed:", error);
        setPhotoUploadStatus("idle");
        setError(
          intl.formatMessage({
            id: "error.guestForm.uploadFailed",
            defaultMessage: "Falha no upload da foto. Tente novamente.",
          })
        );
      }
    },
    [photoStoragePath, intl]
  );

  const handleCropCancel = useCallback(() => {
    setShowCropper(false);
    setSelectedPhoto(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleRemovePhoto = useCallback(() => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
    setPhotoUploadStatus("idle");
    setHasExistingPhoto(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (photoPreview && !hasExistingPhoto) {
      URL.revokeObjectURL(photoPreview);
    }
    if (guest && isEditMode) {
      guest.photoStoragePath = undefined;
    }
  }, [photoPreview, hasExistingPhoto, guest, isEditMode]);

  const handleSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();
      setError("");

      if (!guestName.trim()) {
        const message = intl.formatMessage({
          id: "message.guest.nameRequired",
          defaultMessage: "Nome é obrigatório",
        });
        setError(message);
        return;
      }

      let guestData: Partial<TGuest> = {
        id: guestId,
        eventId: event?.id as string,
        eventCode: event?.code as string,
        name: guestName.trim(),
      };

      const hasValidPhoto = photoPreview && photoUploadStatus === "success";

      guestData.photoStoragePath = hasValidPhoto ? photoStoragePath : null;

      setPhotoUploadStatus("uploading");

      try {
        let guestResult: TGuest;

        if (isEditMode && guest?.id) {
          let updatedGuestData: Partial<TGuest> = {
            ...guestData,
            eventCode: event?.code as string,
            eventId: event?.id as string,
            id: guest.id,
          };
          guestResult = (await updateGuest(
            guest.id,
            updatedGuestData
          )) as TGuest;
        } else {
          guestResult = (await createGuest(guestData)) as TGuest;
        }

        setGuest(guestResult);
        setPhotoUploadStatus("success");

        if (event?.betaFeatures?.includes("feed")) {
          navigate("/feed/v2");
        } else {
          navigate("/options");
        }
      } catch (error) {
        console.error("Save failed:", error);
        setPhotoUploadStatus("idle");
        setError(
          intl.formatMessage({
            id: "error.guestForm.saveFailed",
            defaultMessage: "Falha ao salvar. Tente novamente.",
          })
        );
      }
    },
    [
      guestName,
      guestId,
      event?.code,
      event?.id,
      event?.betaFeatures,
      navigate,
      photoStoragePath,
      photoUploadStatus,
      photoPreview,
      setGuest,
      intl,
      createGuest,
      updateGuest,
      isEditMode,
      guest?.id,
    ]
  );

  const handleSkip = useCallback(async () => {
    const guest = {
      id: guestId,
      eventId: event?.id as string,
      eventCode: event?.code as string,
    };

    const guestCreated = await createGuest(guest);

    setGuest(guestCreated as TGuest);
    if (event?.betaFeatures?.includes("feed")) {
      navigate("/feed/v2");
    } else {
      navigate("/options");
    }
  }, [
    guestId,
    event?.id,
    event?.code,
    event?.betaFeatures,
    createGuest,
    setGuest,
    navigate,
  ]);

  const onGuestNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setGuestName(e.target.value);
    },
    []
  );

  if (showCropper && selectedPhoto) {
    return (
      <div className="template-container px-10 py-16 flex flex-col items-center">
        <h2 className="text-xl mb-4">
          <FormattedMessage
            id="title.guestForm.cropPhoto"
            defaultMessage="Recortar Foto"
          />
        </h2>
        <ImageCropper
          file={selectedPhoto}
          onFinish={handleCropFinish}
          onCancel={handleCropCancel}
          maxZoom={6}
        />
      </div>
    );
  }

  return (
    <div className="template-container px-10 py-16 flex flex-col items-center">
      <h1>
        <FormattedMessage id="title.guestForm" defaultMessage="Seu Perfil" />
      </h1>

      <form className="flex flex-col gap-4 w-full">
        <input
          type="text"
          name="name"
          value={guestName}
          onChange={onGuestNameChange}
          placeholder={intl.formatMessage({
            id: "placeholder.guestForm.name",
            defaultMessage: "Seu nome ou apelido",
          })}
          className={`w-full px-4 py-3 ${theme.input} flex items-center justify-between`}
          required
        />
        {error && <p className="text-red-500">{error}</p>}

        <div className="flex flex-col gap-2">
          {/* Photo Preview */}
          {photoPreview && (
            <div className="flex flex-col items-center gap-2 mb-4 relative">
              <img
                src={photoPreview}
                alt="Preview"
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-300"
              />
              <button
                type="button"
                onClick={handleRemovePhoto}
                className="text-red-500 hover:text-red-700 text-sm absolute -top-4 right-24"
              >
                <Close
                  width={24}
                  height={24}
                  color={"none"}
                  outline={"black"}
                />
              </button>
            </div>
          )}

          {!photoPreview && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
                id="photo-upload"
              />
              <button
                type="button"
                className={`${theme.buttonSecondary} w-full cursor-pointer text-center py-2 px-4`}
                onClick={handlePhotoButton}
              >
                <FormattedMessage
                  id="button.guestForm.selectPhoto"
                  defaultMessage="Selecionar foto (opcional)"
                />
              </button>
            </>
          )}
        </div>

        <div className={`${theme.card} card w-full p-6`}>
          <p className="">
            <FormattedMessage
              id="message.guestForm.dataInfo"
              defaultMessage="Os dados fornecidos não são obrigatórios. Eles serão exibidos apenas como uma assinatura nas fotos e vídeos que você enviar."
            />
          </p>
        </div>

        <button
          type="submit"
          className={`w-full px-4 py-3 ${theme.button} flex items-center justify-between mt-5`}
          onClick={handleSubmit}
          disabled={photoUploadStatus === "uploading"}
        >
          <span className="w-full text-center text-lg no-select">
            {photoUploadStatus === "uploading" ? (
              <FormattedMessage
                id="button.guestForm.uploading"
                defaultMessage="Salvando..."
              />
            ) : (
              <FormattedMessage
                id="button.guestForm.submit"
                defaultMessage="Salvar"
              />
            )}
          </span>
        </button>
        {!isEditMode && (
          <button
            type="button"
            className="py-3 px-10 underline text-lg"
            onClick={handleSkip}
          >
            <FormattedMessage
              id="button.guestForm.skip"
              defaultMessage="Continuar de forma anônima"
            />
          </button>
        )}
      </form>
    </div>
  );
}
